import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Phone, Mail, Globe, Download, Instagram, Facebook, Youtube } from "lucide-react";
import { apiUrl, useApi } from "@/hooks/useApi";

const SpotifyIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.52 17.28c-.22.36-.68.47-1.04.25-2.85-1.74-6.44-2.13-10.66-1.15-.4.09-.8-.16-.89-.56-.09-.4.16-.8.56-.89 4.63-1.06 8.6-.6 11.78 1.35.36.21.47.68.25 1zm1.47-3.27c-.27.45-.85.59-1.29.32-3.26-2-8.24-2.58-12.08-1.42-.5.15-1.02-.13-1.17-.63-.15-.5.13-1.02.63-1.17 4.37-1.33 9.85-.68 13.6 1.61.44.27.58.85.31 1.29zm.13-3.41C15.24 8.4 8.83 8.16 5.16 9.3c-.6.18-1.23-.16-1.41-.76-.18-.6.16-1.23.76-1.41 4.28-1.3 11.28-1.05 15.73 1.61.54.32.72 1.02.4 1.56-.32.54-1.02.72-1.56.4h.04z" />
  </svg>
);

const AppleMusicIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="6.5" cy="17.5" r="3" />
    <circle cx="17.5" cy="15.5" r="3" />
    <path d="M9.5 17.5V6l11-2.5V15.5" />
  </svg>
);

const SOCIAL_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
};

const CardPage = () => {
  const { token } = useParams();
  const { data, loading, error } = useApi(`/card/${token}`);

  useEffect(() => {
    document.title = "Giorgio Panatero — Monferrato Immersive";
    let meta = document.head.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow");
    return () => meta.setAttribute("content", "index, follow");
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-[#0E0D0C]" data-testid="card-loading" />;
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0E0D0C] text-[#F7F5F0] flex items-center justify-center px-6" data-testid="card-not-found">
        <p className="font-serif-display text-2xl text-center">Questa pagina non è disponibile.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0E0D0C] text-[#F7F5F0] flex flex-col items-center px-6 py-14 sm:py-20" data-testid="digital-card-page">
      <div className="w-full max-w-sm flex flex-col items-center">
        <img src="/assets/logo-white.webp" alt="Monferrato Immersive®" className="h-14 w-auto" data-testid="card-logo" />

        <span className="gold-rule-center block w-24 my-8" />

        <h1 className="font-serif-display text-4xl sm:text-5xl text-center leading-tight" data-testid="card-name">
          {data.firstName} {data.lastName}
        </h1>
        <p className="mt-3 text-[11px] tracking-[0.3em] uppercase text-[#C5A059] font-medium" data-testid="card-role">
          {data.role} · {data.organization}
        </p>

        <div className="w-full mt-10 space-y-3.5">
          <a
            href={apiUrl(`/card/${token}/vcf`)}
            download
            data-testid="card-save-contact-button"
            className="flex items-center justify-center gap-3 w-full bg-[#C5A059] text-[#121212] py-4 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-[#D8B46C] active:scale-[0.99] transition-all duration-300"
          >
            <Download size={15} /> Salva il mio contatto
          </a>
          <a
            href={`tel:${data.phoneIntl}`}
            data-testid="card-call-button"
            className="flex items-center justify-center gap-3 w-full border border-[#F7F5F0]/25 py-4 text-[11px] tracking-[0.25em] uppercase hover:border-[#C5A059] hover:text-[#D8B46C] transition-colors duration-300"
          >
            <Phone size={15} /> {data.phoneDisplay}
          </a>
          <a
            href={`mailto:${data.email}`}
            data-testid="card-email-button"
            className="flex items-center justify-center gap-3 w-full border border-[#F7F5F0]/25 py-4 text-[11px] tracking-[0.25em] uppercase hover:border-[#C5A059] hover:text-[#D8B46C] transition-colors duration-300"
          >
            <Mail size={15} /> Email
          </a>
          <a
            href={data.website}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="card-website-button"
            className="flex items-center justify-center gap-3 w-full border border-[#F7F5F0]/25 py-4 text-[11px] tracking-[0.25em] uppercase hover:border-[#C5A059] hover:text-[#D8B46C] transition-colors duration-300"
          >
            <Globe size={15} /> monferratoimmersive.it
          </a>
        </div>

        <span className="gold-rule-center block w-24 my-10" />

        <div className="flex items-center justify-center gap-4" data-testid="card-socials">
          {data.socials.map((s) => {
            const LucideIcon = SOCIAL_ICONS[s.id];
            return (
              <a
                key={s.id}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${s.label} — ${s.network}`}
                data-testid={`card-social-${s.id}`}
                className="w-11 h-11 rounded-full border border-[#C5A059]/40 flex items-center justify-center text-[#F7F5F0]/80 hover:bg-[#C5A059] hover:text-[#121212] hover:border-[#C5A059] transition-colors duration-300"
              >
                {LucideIcon ? <LucideIcon size={17} /> : s.id === "spotify" ? <SpotifyIcon size={17} /> : <AppleMusicIcon size={17} />}
              </a>
            );
          })}
        </div>

        <p className="mt-12 text-[10px] tracking-[0.25em] uppercase text-[#F7F5F0]/35" data-testid="card-footer-note">
          Monferrato Immersive®
        </p>
      </div>
    </div>
  );
};

export default CardPage;
